import { Injectable, NgZone } from "@angular/core";
import { Store } from "@ngrx/store";
import { Subject } from "rxjs";

import { AuthService } from "@core/auth/auth.service";
import { ConfigService } from "@core/config/app.config.service";

import { MENSAJES_NOTIFICACION } from "@shared/constants/apl-afiliacion.constants";
import { AfiliacionesBaseFunctions } from "@shared/functions/afiliaciones/afiliaciones-base.functions";
import {
  EnumIconos,
  UtilPopupService,
} from "@shared/services/util-popup.service";

import { clearStore } from "app/store/actions/app.actions";

@Injectable({
  providedIn: "root",
})
export class InactivityService {
  public onInactivity$ = new Subject<void>();

  private warningTimer?: ReturnType<typeof setTimeout>;
  private logoutTimer?: ReturnType<typeof setTimeout>;
  private autoCloseTimer?: ReturnType<typeof setTimeout>;

  private readonly events = ["mousemove", "keydown", "scroll", "touchstart"];

  private static readonly WARNING_TIMEOUT_MS = 1_740_000; // 29 min
  private static readonly LOGOUT_TIMEOUT_MS = 1_800_000; // 30 min
  private static readonly MODAL_AUTO_CLOSE_MS = 60_000; // 1 min

  constructor(
    private readonly ngZone: NgZone,
    private readonly popupService: UtilPopupService,
    private readonly authService: AuthService,
    private readonly store: Store,
    private readonly notifier: AfiliacionesBaseFunctions,
    private readonly configService: ConfigService
  ) { }

  startMonitoring(): void {
    this.ngZone.runOutsideAngular(() => {
      this.events.forEach((event) =>
        window.addEventListener(event, this.resetTimer)
      );
    });

    this.resetTimer();
  }

  stopMonitoring(): void {
    this.clearAllTimers();
    this.events.forEach((event) =>
      window.removeEventListener(event, this.resetTimer)
    );
  }

  private readonly resetTimer = (): void => {
    this.clearAllTimers();
    this.warningTimer = this.setTimer(
      this.showWarning,
      InactivityService.WARNING_TIMEOUT_MS
    );
    this.logoutTimer = this.setTimer(
      this.showLogoutModal,
      InactivityService.LOGOUT_TIMEOUT_MS
    );
  };

  private readonly showWarning = (): void => {
    const warning = MENSAJES_NOTIFICACION[0].expira_sesion;

    this.notifier.mostrarNotificacion(
      warning.mensaje,
      warning.duracion,
      warning.horizontalPosition,
      warning.verticalPosition
    );
  };

  private readonly showLogoutModal = (): void => {
    this.popupService.mostrarMensajeAel3(
      "Tu sesión ha expirado",
      "Tu sesión ha expirado. Por favor vuelve a ingresar para continuar.",
      EnumIconos.time,
      EnumIconos.logo,
      {
        showCancelButton: false,
        confirmButtonText: "Aceptar",
        mostrarConfirmButton: true,
        ejecutarAccionAlConfirmar: true,
        accionConfirmacion: () => {
          (async () => {
            this.clearAutoCloseTimer();
            await this.logoutUser();
          })();
        }
      }
    );

    this.autoCloseTimer = this.setTimer(() => {
      this.popupService.cerrarMensaje?.();
      this.logoutUser();
    }, InactivityService.MODAL_AUTO_CLOSE_MS);
  };

  private async logoutUser(): Promise<void> {
    this.authService.signOut();
    this.store.dispatch(clearStore());
    window.open(this.configService?.config?.urlEmpleador, "_self");
  }

  private clearAllTimers(): void {
    clearTimeout(this.warningTimer);
    clearTimeout(this.logoutTimer);
    clearTimeout(this.autoCloseTimer);
  }

  private clearAutoCloseTimer(): void {
    clearTimeout(this.autoCloseTimer);
  }

  private setTimer(
    callback: () => void,
    delay: number
  ): ReturnType<typeof setTimeout> {
    return setTimeout(callback, delay);
  }
}
